import { useCallback, useMemo } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  buildObSettingsToCreate,
  getGroupedDisplayObSettings,
  type DisplayObSetting,
} from "./obSettings.logic";
import type { CompanyOBSetting } from "model/Company";
import { Api } from "services/api/api.service";

type SaveObSettingsParams = {
  dateString: string | null;
  dayIndexes: readonly number[];
  endTime: string;
  obType: "Percent" | "Kr";
  obValue: number;
  startTime: string;
  useDate: boolean;
};

export type ObSettingsMutationStatus = "error" | "partial" | "success";

type UseObSettingsResult = {
  dateList: readonly DisplayObSetting[][];
  deleteObSettings(
    sameSettingsList: readonly DisplayObSetting[],
  ): Promise<ObSettingsMutationStatus>;
  saveObSettings(
    params: SaveObSettingsParams,
  ): Promise<ObSettingsMutationStatus>;
  weekList: readonly DisplayObSetting[][];
};

export const useObSettings = (): UseObSettingsResult => {
  const [companyState, , companyDispatch] = useCompanyStore();

  const { dateList, weekList } = useMemo(
    () => getGroupedDisplayObSettings(companyState.obSettings ?? []),
    [companyState.obSettings],
  );

  const refreshObSettings = useCallback(
    async (companyId: number): Promise<boolean> => {
      try {
        const result = await Api().company.getCompanyObSettings(companyId);
        companyDispatch({
          type: "UPDATE_COMPANY_OBSETTINGS",
          payload: result.data,
        });
        return true;
      } catch {
        return false;
      }
    },
    [companyDispatch],
  );

  const deleteObSettings = useCallback(
    async (
      sameSettingsList: readonly DisplayObSetting[],
    ): Promise<ObSettingsMutationStatus> => {
      const companyId = companyState.company?.id;

      if (!companyId) {
        return "error";
      }

      const settingIds = sameSettingsList.flatMap((setting) => setting.id);
      const deleteResults = await Promise.allSettled(
        settingIds.map((settingId) =>
          Api().company.deleteCompanyObSettings(companyId, settingId),
        ),
      );

      const removedIds = settingIds.filter(
        (_settingId, index) => deleteResults[index]?.status === "fulfilled",
      );

      if (removedIds.length === 0) {
        return "error";
      }

      const wasRefreshed = await refreshObSettings(companyId);

      if (!wasRefreshed) {
        return "partial";
      }

      return deleteResults.every((result) => result.status === "fulfilled")
        ? "success"
        : "partial";
    },
    [companyState.company?.id, refreshObSettings],
  );

  const saveObSettings = useCallback(
    async ({
      dateString,
      dayIndexes,
      endTime,
      obType,
      obValue,
      startTime,
      useDate,
    }: SaveObSettingsParams): Promise<ObSettingsMutationStatus> => {
      const companyId = companyState.company?.id;

      if (!companyId) {
        return "error";
      }

      const newObSettings = buildObSettingsToCreate({
        companyId,
        dateString,
        dayIndexes,
        endTime,
        obType,
        obValue,
        startTime,
        useDate,
      });

      if (newObSettings.length === 0) {
        return "error";
      }

      const createResults = await Promise.allSettled(
        newObSettings.map((setting) =>
          Api().company.addCompanyObSetting(companyId, setting),
        ),
      );

      const createdSettings: CompanyOBSetting[] = [];

      createResults.forEach((result) => {
        if (result.status === "fulfilled") {
          createdSettings.push(result.value.data);
        }
      });

      if (createdSettings.length === 0) {
        return "error";
      }

      const wasRefreshed = await refreshObSettings(companyId);

      if (!wasRefreshed) {
        return "partial";
      }

      return createResults.every((result) => result.status === "fulfilled")
        ? "success"
        : "partial";
    },
    [companyState.company?.id, refreshObSettings],
  );

  return {
    dateList,
    deleteObSettings,
    saveObSettings,
    weekList,
  };
};
